// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

interface LogoWordmarkProps {
  className?: string;
  'aria-hidden'?: boolean;
}

export function LogoWordmark({ className, 'aria-hidden': ariaHidden }: LogoWordmarkProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 11700 4483"
      className={className}
      aria-hidden={ariaHidden}
      aria-label={ariaHidden ? undefined : 'Query Health'}
      role={ariaHidden ? undefined : 'img'}
    >
      {/* Q mark */}
      <path
        fill="#0865B1"
        d="M1477 2634c-2,2 -5,3 -8,4 3,-1 6,-2 8,-4z"
      />
      <path
        fill="#006EF9"
        d="M2101 2685c49,-68 90,-141 123,-221 58,-138 88,-286 88,-446 0,-162 -30,-312 -88,-450 -58,-138 -139,-258 -244,-360 -105,-103 -227,-183 -367,-240 -140,-57 -293,-86 -459,-86 -164,0 -316,29 -456,86 -140,57 -262,137 -367,240 -104,102 -186,222 -244,359 -58,137 -87,286 -87,448 0,160 30,308 89,446 59,138 141,259 245,363 105,103 228,184 369,242 141,58 294,87 457,87 152,0 294,-24 424,-74l-372 -372c-19,1 -38,2 -58,2 -97,0 -186,-17 -266,-50 -80,-33 -149,-81 -207,-142 -58,-61 -102,-134 -133,-219 -32,-85 -47,-179 -47,-283 0,-139 27,-260 82,-363 55,-102 132,-182 229,-239 97,-57 212,-86 342,-86 100,0 189,16 269,49 80,32 149,78 207,140 58,61 103,133 134,217 31,84 47,178 47,282 0,118 -21,224 -62,318l-352 -352 -283 283 368 368 330 330 328 328 284 -283 -323 -322z"
      />
      {/* QUERY text letters */}
      <path
        fill="#006EF9"
        d="M2829 2716c77,136 185,243 323,319 138,77 297,115 478,115 180,0 339,-38 476,-115 137,-76 243,-182 320,-317 77,-135 115,-289 115,-464l0 -1334 -491 0 0 1362c0,87 -18,163 -55,226 -36,63 -85,112 -147,146 -63,34 -135,51 -218,51 -79,0 -151,-17 -216,-51 -66,-34 -117,-83 -154,-148 -38,-64 -56,-140 -56,-227l0 -1359 -492 0 0 1331c0,174 39,330 117,465z"
      />
      <path
        fill="#006EF9"
        d="M5016 3116c526,0 1052,0 1578,0l0 -423 -1089 0 0 -471 980 0 0 -408 -980 0 0 -474 1073 0 0 -420c-520,0 -1041,0 -1562,0 0,732 0,1464 0,2196z"
      />
      <path
        fill="#006EF9"
        d="M9601 2202l0 914 488 0c0,-327 0,-654 0,-981l-709 -1215 -567 0 788 1282z"
      />
      <path
        fill="#006EF9"
        d="M7016 920l0 2196 488 0 0 -905 52 0 655 905 578 0 -703 -921c75,-13 143,-35 206,-65 112,-54 199,-129 261,-226 62,-96 93,-209 93,-337 0,-127 -31,-239 -93,-336 -62,-98 -148,-174 -258,-229 -110,-55 -238,-82 -383,-82l-408 0 -115 0 -373 0zm488 930l0 -551 352 0c98,0 172,26 224,78 52,52 78,117 78,196 0,85 -27,153 -80,202 -52,50 -128,75 -225,75l-349 0z"
      />
      {/* Sparkle accents — Teal */}
      <path
        fill="#02C7C3"
        d="M10780 522c14,-126 114,-226 240,-240 7,-1 12,-6 12,-14l0 -1c0,-8 -5,-14 -12,-14 -126,-15 -226,-115 -240,-241 -1,-7 -7,-12 -14,-12l-2 0c-7,0 -13,5 -14,12 -14,126 -114,226 -240,241 -7,0 -13,6 -13,14l0 1c0,8 6,13 13,14 126,14 226,114 240,240 1,8 7,13 14,13l2 0c7,0 13,-5 14,-13zm-191 355c-30,263 -239,472 -502,502 -15,2 -26,14 -26,29l0 4c0,15 11,27 26,29 263,30 472,239 502,502 2,15 14,26 29,26l4 0c15,0 27,-11 29,-26 30,-263 239,-472 502,-502 15,-2 26,-14 26,-29l0 -4c0,-15 -11,-27 -26,-29 -263,-30 -472,-239 -502,-502 -2,-15 -14,-26 -29,-26l-4 0c-15,0 -27,11 -29,26zm298 -107c196,22 351,177 374,374 1,11 10,19 21,19l3 0c11,0 20,-8 22,-19 22,-197 178,-352 374,-374 11,-2 19,-11 19,-22l0 -3c0,-11 -8,-20 -19,-22 -196,-22 -352,-178 -374,-374 -2,-11 -11,-19 -22,-19l-3 0c-11,0 -20,8 -21,19 -23,196 -178,352 -374,374 -12,2 -20,11 -20,22l0 3c0,11 8,20 20,22z"
      />
      {/* HEALTHCARE tagline — Teal */}
      <path
        fill="#02C7C3"
        d="M9573 3887l0 596 151 0 0 -242 214 0 0 242 151 0 0 -596 -151 0 0 228 -214 0 0 -228 -151 0zm-640 129l0 467 151 0 0 -467 182 0 0 -129 -515 0 0 129 182 0zm-805 -129l0 596 120 0 31 0 256 0 0 -130 -256 0 0 -466 -151 0zm-668 0l-232 596 154 0 37 -104 222 0 36 104 157 0 -229 -596 -145 0zm1 372l70 -199 69 199 -139 0zm-955 -372l0 596 119 0 31 0 292 0 0 -127 -292 0 0 -115 262 0 0 -124 -262 0 0 -103 287 0 0 -127 -287 0 -31 0 -119 0zm-345 596l0 -596 -151 0 0 228 -214 0 0 -228 -151 0 0 596 151 0 0 -242 214 0 0 242 151 0z"
      />
      {/* Separator line */}
      <polygon fill="#006EF9" points="0,4107 5185,4107 5185,4263 0,4263" />
    </svg>
  );
}
